/**
 * Maison Montrose — offre DOUCEUR79 : « Votre Crème de Jour Anti-Âge offerte dès 79 € d'achat ».
 *
 * (Les identifiants `eclat79` / `MonEclat79` / `_eclat79` datent de la première offre de ce
 * type, ECLAT79 ; ils sont conservés pour ne pas casser le panier. « Sérum » dans les noms de
 * fonctions désigne simplement le produit offert.)
 *
 * Côté Shopify, l'offre est une réduction « Achetez X, obtenez Y » : dès 79 € de produits
 * éligibles, une Crème de Jour Anti-Âge passe à 0 €. Shopify ne dépose jamais lui-même le produit
 * offert dans le panier : tant que la crème n'y est pas, le code est gardé sur le panier mais
 * marqué « non applicable », et le thème affichait « code invalide ».
 *
 * Règle voulue par le client (4 octobre 2026) : la crème n'est déposée qu'au moment précis
 * où le client tape le code dans le champ « Code de réduction », jamais après coup.
 *
 * Ce module tient le panier en accord avec l'offre, quelle que soit la manipulation :
 *   - dépose la crème (propriété privée `_eclat79`) quand le code est tapé et que le seuil
 *     est atteint (prepareGiftForCode, appelé par assets/cart-discount.js) — Shopify
 *     applique alors lui-même la gratuité (vérifiée jusqu'au checkout) ;
 *   - retire la crème offerte dès que le code n'est plus applicable (panier repassé sous 79 €,
 *     code retiré, offre terminée), pour que personne ne le paie ;
 *   - retire le code lui-même quand Shopify ne l'applique pas (montant sous 79 €, crème
 *     offerte supprimée à la main, code resté d'une visite précédente) et l'explique au
 *     client, qui le retapera une fois le montant atteint ;
 *   - ramène le cadeau à une seule unité si les quantités sont manipulées.
 *
 * La gratuité n'est jamais simulée : c'est toujours Shopify qui décide, ce script ne fait
 * qu'ajouter ou retirer la ligne, puis redessine le panier.
 *
 * Configuration : `window.MonEclat79`, posée par snippets/mon-eclat79.liquid.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const config = window.MonEclat79 || null;

/**
 * Panier complet (réponse Ajax brute) transmis par un autre script juste après une mise à
 * jour : la prochaine réconciliation l'utilise au lieu de relire /cart.js (après une mise à
 * jour de code, cette relecture prend souvent plus d'une seconde côté Shopify).
 * @type {any}
 */
let pendingSeed = null;
/** Vrai dès qu'un évènement panier est arrivé et n'a pas encore été réconcilié. */
let dirty = true;

const LINES_UPDATE = 'shopify:cart:lines-update';
const DISCOUNT_UPDATE = 'shopify:cart:discount-update';
const SOURCE = 'mon-eclat79';
const OWN_EVENT = Symbol('mon-eclat79');

/** @param {unknown} value */
export function normalizeCode(value) {
  return String(value ?? '')
    .trim()
    .toUpperCase();
}

function routes() {
  return /** @type {any} */ (window).Theme?.routes ?? {};
}

/**
 * @param {string | undefined} url
 * @param {string} fallback
 */
function jsonRoute(url, fallback) {
  const base = url || fallback;
  return base.endsWith('.js') ? base : `${base}.js`;
}

/** @param {any} item */
export function isGiftLine(item) {
  if (!config) return false;
  const value = item?.properties?.[config.property];
  return value != null && value !== '';
}

/** @param {any} item */
export function isSerumLine(item) {
  if (!config) return false;
  return Number(item?.variant_id) === Number(config.variantId) || (config.handle && item?.handle === config.handle);
}

/** @param {any} item */
export function isFreeLine(item) {
  return Number(item?.original_line_price) > 0 && Number(item?.final_line_price) === 0;
}

/**
 * Lit l'état de l'offre dans un panier Ajax (/cart.js).
 *
 * `eligible` reproduit le calcul de Shopify pour le seuil : la somme des lignes, moins
 * l'unité du produit offert (cette unité ne compte jamais pour les 79 €).
 *
 * @param {any} cart
 */
export function analyze(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const giftLines = items.filter(isGiftLine);
  const serumLines = items.filter(isSerumLine);
  const total = items.reduce((sum, item) => sum + Number(item.original_line_price || 0), 0);
  const serumUnit = serumLines.length ? Number(serumLines[0].original_price || 0) : 0;
  const eligible = total - serumUnit;
  const threshold = Number(config?.threshold || 0);
  const entry = (Array.isArray(cart?.discount_codes) ? cart.discount_codes : []).find(
    (discount) => normalizeCode(discount?.code) === normalizeCode(config?.code)
  );

  return {
    items,
    giftLines,
    serumLines,
    giftQuantity: giftLines.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    total,
    eligible,
    threshold,
    remaining: Math.max(0, threshold - eligible),
    reachesThreshold: eligible >= threshold,
    requested: Boolean(entry),
    applicable: Boolean(entry && entry.applicable),
    freeLines: items.filter(isFreeLine),
  };
}

/**
 * Vrai si `data` est un panier complet (réponse de /cart.js, /cart/update ou /cart/change),
 * et non la réponse partielle de /cart/add.js qui ne liste que les lignes ajoutées.
 * @param {any} data
 */
export function isFullCart(data) {
  return Boolean(
    data &&
      Array.isArray(data.items) &&
      Array.isArray(data.discount_codes) &&
      typeof data.item_count === 'number'
  );
}

/** Ids des sections qui affichent le panier sur la page courante (tiroir et/ou page panier). */
export function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
    const id = /** @type {HTMLElement} */ (element).dataset.sectionId;
    if (id) ids.add(id);
  });
  return Array.from(ids);
}

/**
 * @param {string} url
 * @param {Record<string, unknown>} [body]
 * @param {string[]} [sections]
 */
async function request(url, body, sections = []) {
  const payload = { ...(body || {}) };
  if (sections.length) {
    payload.sections = sections.join(',');
    payload.sections_url = window.location.pathname;
  }
  const response = await fetch(url, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(payload) : undefined,
    credentials: 'same-origin',
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.description || data?.message || `Cart request failed (${response.status})`);
  }
  return data;
}

export function fetchCart() {
  return request(jsonRoute(routes().cart_url, '/cart'));
}

/**
 * Dépose une unité du produit offert dans le panier.
 * @param {string[]} [sections]
 */
export function addGift(sections = []) {
  if (!config) return Promise.reject(new Error('DOUCEUR79 non configuré'));
  const properties = {};
  properties[config.property] = '1';
  return request(
    jsonRoute(routes().cart_add_url, '/cart/add'),
    { items: [{ id: config.variantId, quantity: 1, properties }] },
    sections
  );
}

/**
 * Change la quantité d'une ligne, désignée par son numéro (1 = première ligne).
 * @param {number} line
 * @param {number} quantity
 * @param {string[]} [sections]
 */
function changeLine(line, quantity, sections = []) {
  return request(jsonRoute(routes().cart_change_url, '/cart/change'), { line, quantity }, sections);
}

/**
 * Retire toutes les lignes offertes déposées par ce script.
 * @param {any} cart - Panier tel que renvoyé par /cart.js
 * @param {string[]} [sections]
 * @returns {Promise<any | null>} La dernière réponse panier, ou null si rien à retirer.
 */
export async function removeGiftLines(cart, sections = []) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const lines = items
    .map((item, index) => (isGiftLine(item) ? index + 1 : 0))
    .filter(Boolean)
    .sort((a, b) => b - a); // de la dernière à la première : les numéros ne bougent pas
  let data = null;
  for (const line of lines) {
    data = await changeLine(line, 0, line === lines[lines.length - 1] ? sections : []);
  }
  return data;
}

/**
 * Retire le code de l'offre du panier (les autres codes éventuels sont conservés) et
 * redessine les sections avec la réponse. Même appel que le bouton « × » du thème.
 * @param {any} cart - Panier tel que renvoyé par /cart.js
 * @param {string[]} [sections]
 */
export async function dropCode(cart, sections = []) {
  const others = (Array.isArray(cart?.discount_codes) ? cart.discount_codes : [])
    .map((discount) => String(discount?.code ?? ''))
    .filter((code) => code && normalizeCode(code) !== normalizeCode(config?.code));
  const data = await request(routes().cart_update_url || '/cart/update', { discount: others.join(',') }, sections);
  return announce(data);
}

/**
 * Ramène le cadeau à une unité : on garde de préférence une ligne déjà offerte.
 * @param {any} cart
 */
async function clampGift(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const giftLines = items.filter(isGiftLine);
  const keep = giftLines.find(isFreeLine) ?? giftLines[0];
  const extras = giftLines
    .filter((item) => item !== keep)
    .map((item) => items.indexOf(item) + 1)
    .sort((a, b) => b - a);
  for (const line of extras) await changeLine(line, 0);
  if (keep && Number(keep.quantity) > 1) await changeLine(items.indexOf(keep) + 1, 1);
}

/**
 * Prépare le panier juste avant que le thème envoie le code à Shopify :
 * si le seuil est atteint et que le produit offert n'est pas dans le panier, on le dépose.
 * Utilisé par assets/cart-discount.js.
 */
export async function prepareGiftForCode() {
  // Si aucun évènement panier n'est arrivé depuis la dernière réconciliation, l'état en
  // cache est à jour : on s'épargne une lecture de /cart.js avant de déposer le cadeau.
  const fresh = !dirty && lastState && Date.now() - lastStateAt < 60000;
  const state = fresh ? lastState : analyze(await fetchCart());
  if (state.reachesThreshold && state.serumLines.length === 0) {
    await addGift();
    return { ...state, added: true };
  }
  return { ...state, added: false };
}

/**
 * Redessine le panier partout où il est affiché et prévient le reste du thème
 * (compteur du header, tiroir) via l'évènement standard, sans ouvrir le tiroir.
 * @param {string[]} sections
 */
async function publish(sections) {
  return announce(await request(jsonRoute(routes().cart_update_url, '/cart/update'), {}, sections));
}

/**
 * Prévient le thème avec un panier déjà reçu (réponse de /cart/change ou /cart/update
 * demandée avec `sections`) : les sections sont redessinées sans requête supplémentaire.
 * @param {any} data
 */
function announce(data) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({
    action: 'update',
    context: 'cart',
    lines: [],
    promise: deferred.promise,
  });
  /** @type {any} */ (event)[OWN_EVENT] = true;
  document.dispatchEvent(event);
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
    detail: {
      sections: data.sections,
      items: data.items,
      itemCount: data.item_count,
      source: SOURCE,
      didError: false,
    },
  });
  return data;
}

/** @param {number} amount - montant en centimes */
function money(amount) {
  try {
    return formatMoney(amount, config?.moneyFormat || '{{amount}} €', config?.currency || 'EUR');
  } catch (error) {
    return `${(amount / 100).toFixed(2).replace('.', ',')} €`;
  }
}

const ICON_GIFT =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>';
const ICON_CHECK =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

/**
 * Affiche l'état de l'offre sous le champ « code de réduction » (emplacements
 * `[data-mon-eclat79-status]`, voir snippets/mon-eclat79-status.liquid).
 * @param {ReturnType<typeof analyze>} state
 */
let lastStatusKey = '';

/**
 * Avis à garder affiché quand le code n'est plus sur le panier (il vient d'être refusé ou
 * retiré). Il reste tant que le montant éligible ne change pas, puis disparaît.
 * @type {{ kind: string, html: string, eligible: number } | null}
 */
let notice = null;

/**
 * Pose un avis explicatif sous le champ « code de réduction ».
 * @param {'under-threshold' | 'dropped'} kind
 * @param {ReturnType<typeof analyze>} state
 */
export function renderNotice(kind, state) {
  const code = config?.code || '';
  const text =
    kind === 'under-threshold'
      ? `Plus que <strong>${money(state.remaining)}</strong> d'achat pour profiter de l'offre. Ajoutez des produits, puis saisissez à nouveau le code ${code}.`
      : `Le code ${code} a été retiré. Pour recevoir la crème offerte, il faut <strong>${money(state.threshold)}</strong> d'achat : saisissez à nouveau le code une fois ce montant atteint.`;
  notice = {
    kind,
    eligible: state.eligible,
    html: `<p class="mon-eclat79-status mon-eclat79-status--pending" role="status"><span class="mon-eclat79-status__icon">${ICON_GIFT}</span><span>${text}</span></p>`,
  };
  renderStatus(state);
}

export function renderStatus(state) {
  const slots = document.querySelectorAll('[data-mon-eclat79-status]');
  if (!slots.length) return;

  let html = '';
  let statusKey = '';
  if (state.requested && state.applicable) {
    html = `<p class="mon-eclat79-status mon-eclat79-status--unlocked" role="status"><span class="mon-eclat79-status__icon">${ICON_CHECK}</span><span>Votre Crème de Jour Anti-Âge offerte est dans votre panier.</span></p>`;
    statusKey = 'unlocked';
    notice = null;
  } else if (state.requested && !state.applicable && state.remaining > 0) {
    // Passage transitoire : le code vient d'être posé sous le seuil, il va être retiré.
    html = `<p class="mon-eclat79-status mon-eclat79-status--pending" role="status"><span class="mon-eclat79-status__icon">${ICON_GIFT}</span><span>Plus que <strong>${money(state.remaining)}</strong> pour recevoir votre Crème de Jour Anti-Âge offerte.</span></p>`;
    statusKey = `pending:${state.remaining}`;
  } else if (notice && notice.eligible === state.eligible) {
    html = notice.html;
    statusKey = `notice:${notice.kind}:${notice.eligible}`;
  } else {
    notice = null;
  }

  // Le volet « Réduction » ne s'ouvre que lorsque l'état de l'offre change : un
  // simple re-rendu du panier ne doit pas le rouvrir si le client l'a refermé.
  const shouldOpen = Boolean(html) && statusKey !== lastStatusKey;
  lastStatusKey = statusKey;

  slots.forEach((slot) => {
    const element = /** @type {HTMLElement} */ (slot);
    if (element.innerHTML !== html) element.innerHTML = html;
    element.hidden = html === '';
    if (shouldOpen) {
      const details = element.closest('details');
      if (details && !details.open) details.open = true;
    }
  });
}

/** @type {ReturnType<typeof analyze> | null} */
let lastState = null;
let lastStateAt = 0;

/**
 * Le thème re-dessine le panier de façon asynchrone (morph) et le message d'état est
 * alors remplacé par son emplacement vide : on le repose un peu plus tard, deux fois.
 */
function refreshStatusLater() {
  [350, 1100].forEach((delay) => {
    window.setTimeout(() => {
      if (lastState) renderStatus(lastState);
    }, delay);
  });
}

let running = false;
let queued = false;

/**
 * Remet le panier d'accord avec l'offre. Idempotent : ne fait rien quand tout est cohérent.
 */
export async function reconcile() {
  if (!config) return;
  if (running) {
    queued = true;
    return;
  }
  running = true;
  dirty = false;
  const seed = pendingSeed;
  pendingSeed = null;

  try {
    // Un panier complet reçu d'une mise à jour qui vient d'avoir lieu évite une relecture.
    let cart = isFullCart(seed) ? seed : await fetchCart();
    let state = analyze(cart);
    const sections = sectionIds();

    if (state.giftQuantity > 1) {
      // 1. Jamais plus d'un produit offert.
      await clampGift(cart);
      cart = await publish(sections);
      state = analyze(cart);
    }

    if (state.giftQuantity >= 1 && !(state.requested && state.applicable)) {
      // 2. L'offre ne s'applique plus (code retiré, panier sous le seuil, offre terminée) :
      //    on retire le cadeau avant que le client ne le paie. La réponse du retrait,
      //    demandée avec les sections, sert directement à redessiner : une seule requête.
      const removed = await removeGiftLines(cart, sections);
      cart = isFullCart(removed) && removed.sections ? announce(removed) : await publish(sections);
      state = analyze(cart);
    }

    if (state.requested && !state.applicable) {
      // 3. Le code est sur le panier mais Shopify ne l'applique pas (montant sous 79 €,
      //    crème offerte supprimée à la main, code resté d'une visite précédente…). La
      //    crème n'est jamais déposée après coup : on retire le code et on l'explique,
      //    le client le retapera une fois le montant atteint.
      cart = await dropCode(cart, sections);
      state = analyze(cart);
      renderNotice('dropped', state);
    }

    // Après le re-rendu : le message d'état, recalculé depuis le panier réel.
    lastState = state;
    lastStateAt = Date.now();
    // Tout de suite (sans attendre une image : l'emplacement survit au morph), puis encore
    // un peu plus tard par sécurité, au cas où une section serait redessinée entre-temps.
    renderStatus(state);
    refreshStatusLater();
  } catch (error) {
    console.warn('[DOUCEUR79]', error);
  } finally {
    running = false;
    if (queued) {
      queued = false;
      schedule(50);
    }
  }
}

let timer = 0;
/** @param {number} [delay] */
function schedule(delay = 80) {
  window.clearTimeout(timer);
  timer = window.setTimeout(() => {
    reconcile();
  }, delay);
}

/**
 * Réconcilie tout de suite à partir d'un panier complet que l'appelant vient de recevoir
 * (réponse brute de /cart/update ou /cart/change), sans relire /cart.js.
 * Utilisé par assets/cart-discount.js après l'ajout ou le retrait d'un code.
 * @param {any} cart
 */
export function reconcileWith(cart) {
  if (!config) return;
  if (isFullCart(cart)) pendingSeed = cart;
  schedule(0);
}

/** @param {Event} event */
function onCartEvent(event) {
  if (/** @type {any} */ (event)[OWN_EVENT]) return;
  dirty = true;
  const promise = /** @type {any} */ (event).promise;
  if (promise && typeof promise.then === 'function') {
    promise.then(() => schedule(), () => schedule());
  } else {
    schedule();
  }
}

if (config && !(/** @type {any} */ (window).__monEclat79Started)) {
  /** @type {any} */ (window).__monEclat79Started = true;
  document.addEventListener(LINES_UPDATE, onCartEvent);
  document.addEventListener(DISCOUNT_UPDATE, onCartEvent);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) schedule();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => schedule(150));
  } else {
    schedule(150);
  }
}
